/** Register page — wired to /api/auth/register. */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Mail, User as UserIcon } from "lucide-react";
import {
  Container,
  Section,
  Eyebrow,
  AccentItalic,
  Button,
  Input,
  FormField,
  ArtCard,
  Alert,
} from "../../components/ui";
import { BrandMark } from "../../components/ui/Brand";
import { useAuth } from "./AuthContext";

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await register({ name: form.name.trim(), email: form.email.trim(), password: form.password });
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section padded="sm" data-testid="register-page">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          <div className="max-w-[440px] w-full mx-auto lg:mx-0 self-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-10">
              <BrandMark size={36} />
              <span className="font-display text-[20px]">Nikhil Vatika Lab</span>
            </Link>

            <Eyebrow>Join the campus</Eyebrow>
            <h1
              className="font-display text-[clamp(34px,4vw,46px)] tracking-[-0.01em] leading-[1.05] mt-4"
              data-testid="register-heading"
            >
              Begin your <AccentItalic>study</AccentItalic>.
            </h1>
            <p className="text-text-dim mt-3 text-[14.5px] leading-relaxed">
              Create an account to enroll in courses, track your sadhana, access the library and
              save your progress.
            </p>

            {err && (
              <Alert variant="error" className="mt-6" data-testid="register-error">
                {err}
              </Alert>
            )}

            <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} data-testid="register-form">
              <FormField id="name" label="Full name" required>
                <div className="relative">
                  <UserIcon size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-faint" />
                  <Input
                    id="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Your name"
                    className="pl-10"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="register-name-input"
                    minLength={2}
                  />
                </div>
              </FormField>

              <FormField id="email" label="Email" required>
                <div className="relative">
                  <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-faint" />
                  <Input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@email.com"
                    className="pl-10"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="register-email-input"
                  />
                </div>
              </FormField>

              <FormField id="password" label="Password" required help="At least 8 characters">
                <div className="relative">
                  <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-faint" />
                  <Input
                    id="password"
                    type="password"
                    required
                    autoComplete="new-password"
                    placeholder="Choose a strong password"
                    className="pl-10"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="register-password-input"
                    minLength={8}
                  />
                </div>
              </FormField>

              <Button type="submit" className="mt-2" disabled={submitting} data-testid="register-submit-btn">
                {submitting ? "Creating account…" : "Create account"} <ArrowRight size={16} />
              </Button>
            </form>

            <p className="text-[13.5px] text-text-dim mt-6 text-center">
              Already a member?{" "}
              <Link to="/login" className="text-mauve font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </div>

          <div className="relative hidden lg:block rounded-[28px] overflow-hidden border border-line-2 min-h-[640px]">
            <ArtCard seed={1} motifSeed={3} />
            <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
              <Eyebrow>
                <span className="text-neutral/80">Why study here</span>
              </Eyebrow>
              <ul className="mt-6 space-y-3 text-[14.5px] text-neutral">
                <li>· Lineage-verified curriculum across 10 traditions</li>
                <li>· 40,000-volume digital library</li>
                <li>· Live satsang & advanced sadhana cohorts</li>
                <li>· Research center publishing peer-reviewed work</li>
                <li>· Certificates issued on completion</li>
              </ul>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
